import React from 'react';
import {
    useQuery, useMutation, useLazyQuery, useApolloClient,
} from '@apollo/client';
import { getCartId } from '@helper_cartid';
import { getLoginInfo } from '@helper_auth';
import * as Schema from '@core_modules/product/services/graphql/schema';
import * as ActionSchema from '@core_modules/product/services/graphql/actionSchema';
import * as CustomizableSchema from '@core_modules/product/services/graphql/customizableSchema';
import ensureCartId from '@core_modules/product/services/graphql/ensureCartId';

const defaultConfig = {

};

const useInternal = {
    context: {
        request: 'internal',
    },
};

const useCartMutation = (mutation) => {
    const client = useApolloClient();
    const [mutate, result] = useMutation(mutation, {
        context: {
            request: 'internal',
        },
    });

    const mutateWithCart = React.useCallback(async (options = {}) => {
        const variables = { ...(options.variables || {}) };
        if (!variables.cartId) {
            const cartId = await ensureCartId(client);
            if (!cartId) {
                throw new Error('Unable to prepare the cart. Please try again.');
            }
            variables.cartId = cartId;
        }
        return mutate({
            ...options,
            variables,
        });
    }, [client, mutate]);

    return [mutateWithCart, result];
};

export const getProduct = (config = {}, options = {}) => useQuery(Schema.getProduct(config), {
    ...defaultConfig,
    ...options,
});

export const getProductPrice = (config = {}, options = {}) => useLazyQuery(Schema.getProductPrice(config), {
    ...defaultConfig,
    ...options,
    ...useInternal,
    fetchPolicy: 'no-cache',
});

export const getProductLabel = (config = {}, options = {}) => useQuery(Schema.getProductLabel(config), {
    ...defaultConfig,
    ...options,
});

export const getRelatedProduct = (config, options = {}) => useQuery(Schema.getRelatedProduct(config), {
    ...defaultConfig,
    ...options,
});

export const getUpsellProduct = (config = {}, options = {}) => useQuery(Schema.getUpsellProduct(config), {
    ...defaultConfig,
    ...options,
});

export const getCustomizableOption = (urlpath) => useLazyQuery(CustomizableSchema.getCustomizableOption(urlpath), {
    ...defaultConfig,
    fetchPolicy: 'no-cache',
});

export const smartProductTabs = (params) => useLazyQuery(Schema.smartProductTabs(), {
    ...defaultConfig,
    variables: {
        ...params,
    },
    skip: typeof window === 'undefined',
    fetchPolicy: 'cache-and-network',
});

export const getProductBySku = (config, params) => useQuery(Schema.getProductBySku(config), {
    ...params,
    ...defaultConfig,
    context: {
        request: 'internal',
    },
});

export const getReviews = (params) => useQuery(Schema.getReview(), {
    variables: {
        ...params,
    },
    fetchPolicy: 'cache-and-network',
});

export const addReview = () => useMutation(Schema.addReview, {
    context: {
        request: 'internal',
    },
});

export const addWishlist = () => useMutation(Schema.addWishlist, {
    context: {
        request: 'internal',
    },
});

export const getConfigurableProduct = (config = {}, params = {}) => useQuery(Schema.getConfigurableProduct(config), {
    ...defaultConfig,
    ...params,
});

export const getBundleProduct = (sku) => useQuery(Schema.getBundleProduct(sku), {
    ...defaultConfig,
});

export const getDownloadProduct = (sku) => useQuery(Schema.getDownloadProduct(sku), {
    ...defaultConfig,
});

export const getGroupedProduct = (config = {}, params = {}) => useQuery(Schema.getGroupedProduct(config), {
    ...defaultConfig,
    ...params,
});

export const getSeller = (options = {}) => useQuery(Schema.getSeller, { ...options, ...useInternal });

// actions add to cart

export const addSimpleProductsToCart = () => useCartMutation(ActionSchema.addSimpleProductsToCart);

export const addVirtualProductToCart = () => useCartMutation(ActionSchema.addVirtualProductToCart);

export const addDownloadProductToCart = () => useCartMutation(ActionSchema.addDownloadableProductsToCart);

export const addConfigProductsToCart = () => useCartMutation(ActionSchema.addConfigProductsToCart);

export const addConfigurableProductsToCart = () => useCartMutation(ActionSchema.addConfigurableProductsToCart);

export const addBundleProductsToCart = () => useCartMutation(ActionSchema.addBundleProductsToCart);

export const addGiftCardProductsToCart = () => useCartMutation(ActionSchema.addGiftCardProductsToCart);

export const getGuestCartId = () => useMutation(ActionSchema.createCartIdGuest, {
    context: {
        request: 'internal',
    },
});
export const getCustomerCartId = () => useQuery(ActionSchema.getCartIdUser, {
    context: {
        request: 'internal',
    },
    fetchPolicy: 'no-cache',
    skip: typeof window === 'undefined' || !getLoginInfo() || !!getCartId(),
});

export const addProductsToCart = () => useCartMutation(ActionSchema.addProductToCart);

export const buyNow = () => useCartMutation(ActionSchema.buyNow);

export const addProductAlertStock = () => useMutation(ActionSchema.addProductAlertStock, {
    context: {
        request: 'internal',
    },
});

export const getProductBannerLite = (urlpath, options = {}) => useLazyQuery(Schema.getProductBannerLite(urlpath), {
    ...defaultConfig,
    ...options,
});

export const createCompareList = () => useMutation(Schema.createCompareList, {
    context: {
        request: 'internal',
    },
});

export const addProductsToCompareList = () => useMutation(Schema.addProductsToCompareList, {
    context: {
        request: 'internal',
    },
});
export const getPriceInstallmentLazy = (options = {}) => useLazyQuery(Schema.getPriceInstallment, {
    ...options,
    ...useInternal,
});
export const getAvailableCouponList = () => useLazyQuery(Schema.getAvailableCouponList, {
    context: {
        request: 'internal',
    },
    fetchPolicy: 'no-cache',
});

export default { getProduct };
